import { useState, useCallback, useEffect, useMemo } from "react";
import i18n from "i18next";
import { isNotificationRead } from "../utils/notifications/notificationRead";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { PAGE_SIZE } from "../constants/pagination";
import webSocketService from "../services/websocket.service";
import { AppDispatch, RootState } from "../store";
import {
  addToast,
  clearAllToasts,
  clearError,
  clearFilters,
  clearSelection,
  deleteNotification,
  fetchNotifications,
  fetchSettings,
  markAllAsRead,
  markAsRead,
  removeToast,
  selectAllNotifications,
  selectConnectionStatus,
  selectFilters,
  selectGroups,
  selectIsLoading,
  selectNotifications,
  selectToasts,
  selectUISettings,
  selectUnreadCount,
  setFilters,
  setPanelOpen,
  setSearchQuery,
  toggleNotificationSelection,
  togglePanel,
  updateSettings,
} from "../store/slices/notificationsSlice";
import {
  INotification,
  INotificationFilters,
  INotificationSettings,
  IToastNotification,
  NotificationPriority,
  NotificationStatus,
  NotificationType,
} from "../types/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import notificationService from "../services/notification.service";

/**
 * Hook для управления системой уведомлений Event Master
 * Фаза 2: Система уведомлений (Неделя 7: Архитектура уведомлений)
 */

// Ключи для React Query
export const NOTIFICATIONS_QUERY_KEYS = {
  all: ["notifications"] as const,
  lists: () => [...NOTIFICATIONS_QUERY_KEYS.all, "list"] as const,
  list: (params?: any) =>
    [...NOTIFICATIONS_QUERY_KEYS.lists(), params] as const,
  details: () => [...NOTIFICATIONS_QUERY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...NOTIFICATIONS_QUERY_KEYS.details(), id] as const,
  unreadCount: () => [...NOTIFICATIONS_QUERY_KEYS.all, "unreadCount"] as const,
  preferences: () => [...NOTIFICATIONS_QUERY_KEYS.all, "preferences"] as const,
};

// Параметры для получения уведомлений
export interface NotificationFilters {
  page?: number;
  limit?: number;
  type?: NotificationType;
  status?: NotificationStatus;
  priority?: NotificationPriority;
  search?: string;
  fromDate?: string;
  toDate?: string;
}

interface UseNotificationsReturn {
  // Данные
  notifications: INotification[];
  groups: ReturnType<typeof selectGroups>;
  unreadCount: number;
  toasts: ReturnType<typeof selectToasts>;

  // Состояние
  isLoading: boolean;
  connectionStatus: ReturnType<typeof selectConnectionStatus>;
  filters: ReturnType<typeof selectFilters>;
  uiSettings: ReturnType<typeof selectUISettings>;

  // Действия с уведомлениями
  loadNotifications: (options?: {
    filters?: INotificationFilters;
    cursor?: string;
    limit?: number;
  }) => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
  markNotificationsAsRead: (notificationIds: string[]) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  deleteSelectedNotifications: (notificationIds: string[]) => Promise<void>;

  // Настройки
  loadSettings: () => Promise<void>;
  updateNotificationSettings: (
    settings: Partial<INotificationSettings>,
  ) => Promise<void>;

  // Фильтрация и сортировка
  updateFilters: (newFilters: INotificationFilters) => void;
  updateSearchQuery: (query: string) => void;
  resetFilters: () => void;

  // UI управление
  toggleNotificationPanel: () => void;
  setNotificationPanelOpen: (open: boolean) => void;
  toggleSelection: (notificationId: string) => void;
  selectAll: () => void;
  clearSelections: () => void;

  // Toast управление
  showToast: (toast: Omit<IToastNotification, "id">) => void;
  hideToast: (id: string) => void;
  clearToasts: () => void;

  // WebSocket управление
  isConnected: boolean;
  connectWebSocket: () => void;
  disconnectWebSocket: () => void;

  // Утилиты
  getUnreadByType: (type: NotificationType) => number;
  getNotificationsByStatus: (status: NotificationStatus) => INotification[];
  getNotificationsByPriority: (
    priority: NotificationPriority,
  ) => INotification[];
  clearErrors: () => void;

  // Дополнительные данные
  total: number;
  hasMore: boolean;
  nextCursor: string;
  settings: INotificationSettings;
  stats: any;
  error: string | null;
  panelOpen: boolean;
  selectedNotifications: string[];
  hasSelectedNotifications: boolean;
  allNotificationsSelected: boolean;
  notificationsByType: { [key in NotificationType]?: number };
  notificationsByPriority: { [key in NotificationPriority]?: number };
  unreadByType: { [key in NotificationType]?: number };

  // Браузерные уведомления
  requestBrowserNotificationPermission: () => Promise<NotificationPermission>;

  // Операции (React Query mutations)
  markAsRead: ReturnType<typeof useMutation<INotification, Error, string>>;
  markAllAsRead: ReturnType<typeof useMutation<void, Error, void>>;

  // Функции
  refreshNotifications: () => void;
  loadMore: () => void;
  getNotificationById: (id: string) => INotification | undefined;
}

// Тексты тостов — на языке интерфейса в момент показа. Модульный `i18n.t`,
// а не `useTranslation`: `t` в зависимостях пересоздавал бы действия хука
// при смене языка.
const toastText = (key: string): string =>
  String(i18n.t(`common:notifications.toasts.${key}`));

export const useNotifications = (
  _initialFilters?: NotificationFilters,
): UseNotificationsReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const [error, _setError] = useState<string | null>(null);

  // Селекторы
  const notifications = useSelector(selectNotifications);
  const groups = useSelector(selectGroups);
  const unreadCount = useSelector(selectUnreadCount);
  void unreadCount;
  const toasts = useSelector(selectToasts);
  const isLoading = useSelector(selectIsLoading);
  const connectionStatus = useSelector(selectConnectionStatus);
  const filters = useSelector(selectFilters);
  const uiSettings = useSelector(selectUISettings);

  // Дополнительные данные из state
  const total = useSelector(
    (state: RootState) => state.notifications.pagination.total,
  );
  const hasMore = useSelector(
    (state: RootState) => state.notifications.pagination.hasMore,
  );
  const nextCursor = useSelector(
    (state: RootState) => state.notifications.pagination.page,
  );
  const settings = useSelector(
    (state: RootState) => state.notifications.settings,
  );
  const stats = useSelector((state: RootState) => state.notifications.stats);
  const panelOpen = useSelector(
    (state: RootState) => state.notifications.ui.isNotificationPanelOpen,
  );
  const selectedNotificationId = useSelector(
    (state: RootState) => state.notifications.ui.selectedNotificationId,
  );

  const selectedNotifications = useMemo(
    () => (selectedNotificationId ? [selectedNotificationId] : []),
    [selectedNotificationId],
  );

  // Получаем данные о пользователе из auth slice - оптимизировано
  const _user = useSelector(
    (state: RootState) => state.auth.user,
    shallowEqual,
  );
  void _user;
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );

  // Инициализация WebSocket соединения
  useEffect(() => {
    if (isAuthenticated && !webSocketService.isConnected()) {
      webSocketService.connect();
    }
    return () => {
      // Не отключаем WebSocket здесь, чтобы не нарушать другие компоненты
    };
  }, [isAuthenticated]);

  // thunk-и слайса при отказе возвращают rejectWithValue: сам dispatch не
  // бросает, поэтому каждое действие ждёт `.unwrap()` — иначе catch с тостом
  // ошибки недостижим, а тост успеха показывается и при отказе сервера.

  // Загрузка уведомлений
  const loadNotifications = useCallback(
    async (options?: { filters?: INotificationFilters; limit?: number }) => {
      try {
        await dispatch(fetchNotifications(options || {})).unwrap();
      } catch (error) {
        console.error("Ошибка загрузки уведомлений:", error);
        dispatch(
          addToast({
            type: "error",
            title: toastText("loadErrorTitle"),
            message: toastText("loadError"),
            duration: 5000,
          }),
        );
      }
    },
    [dispatch],
  );

  // Загрузка дополнительных уведомлений (пагинация)
  const loadMoreNotifications = useCallback(async () => {
    if (hasMore) {
      try {
        await dispatch(
          fetchNotifications({
            filters,
            pagination: { page: nextCursor + 1, limit: PAGE_SIZE.DEFAULT },
          }),
        ).unwrap();
      } catch (error) {
        console.error("Ошибка загрузки дополнительных уведомлений:", error);
        dispatch(
          addToast({
            type: "error",
            title: toastText("loadErrorTitle"),
            message: toastText("loadMoreError"),
            duration: 5000,
          }),
        );
      }
    }
  }, [dispatch, hasMore, filters, nextCursor]);

  // Отметка как прочитанное
  const markNotificationsAsRead = useCallback(
    async (notificationIds: string[]) => {
      const id = notificationIds[0];
      if (id == null) return;
      try {
        await dispatch(markAsRead(id)).unwrap();
      } catch (error) {
        console.error("Ошибка отметки как прочитанное:", error);
        dispatch(
          addToast({
            type: "error",
            title: toastText("errorTitle"),
            message: toastText("markReadError"),
            duration: 3000,
          }),
        );
      }
    },
    [dispatch],
  );

  // Отметка всех как прочитанные
  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await dispatch(markAllAsRead()).unwrap();
      dispatch(
        addToast({
          type: "success",
          title: toastText("markAllReadSuccessTitle"),
          message: toastText("markAllReadSuccess"),
          duration: 3000,
        }),
      );
    } catch (error) {
      console.error("Ошибка отметки всех как прочитанные:", error);
      dispatch(
        addToast({
          type: "error",
          title: toastText("errorTitle"),
          message: toastText("markAllReadError"),
          duration: 3000,
        }),
      );
    }
  }, [dispatch]);

  // Удаление уведомлений
  const deleteSelectedNotifications = useCallback(
    async (notificationIds: string[]) => {
      try {
        // Тост успеха — только после ответов на все запросы удаления
        await Promise.all(
          notificationIds.map((id) =>
            dispatch(deleteNotification(id)).unwrap(),
          ),
        );
        dispatch(
          addToast({
            type: "success",
            title: toastText("deleteSuccessTitle"),
            message: toastText("deleteSuccess"),
            duration: 3000,
          }),
        );
      } catch (error) {
        console.error("Ошибка удаления уведомлений:", error);
        dispatch(
          addToast({
            type: "error",
            title: toastText("errorTitle"),
            message: toastText("deleteError"),
            duration: 3000,
          }),
        );
      }
    },
    [dispatch],
  );

  // Настройки
  const loadSettings = useCallback(async () => {
    try {
      await dispatch(fetchSettings()).unwrap();
    } catch (error) {
      console.error("Ошибка загрузки настроек:", error);
      dispatch(
        addToast({
          type: "error",
          title: toastText("errorTitle"),
          message: toastText("settingsLoadError"),
          duration: 5000,
        }),
      );
    }
  }, [dispatch]);

  const updateNotificationSettings = useCallback(
    async (settings: Partial<INotificationSettings>) => {
      try {
        await dispatch(updateSettings(settings)).unwrap();
        dispatch(
          addToast({
            type: "success",
            title: toastText("settingsSaveSuccessTitle"),
            message: toastText("settingsSaveSuccess"),
            duration: 3000,
          }),
        );
      } catch (error) {
        console.error("Ошибка обновления настроек:", error);
        dispatch(
          addToast({
            type: "error",
            title: toastText("errorTitle"),
            message: toastText("settingsSaveError"),
            duration: 3000,
          }),
        );
      }
    },
    [dispatch],
  );

  // Фильтрация и сортировка
  const updateFilters = useCallback(
    (newFilters: INotificationFilters) => {
      dispatch(setFilters(newFilters));
      // Перезагружаем уведомления с новыми фильтрами
      dispatch(fetchNotifications({ filters: newFilters }));
    },
    [dispatch],
  );

  const updateSearchQuery = useCallback(
    (query: string) => {
      dispatch(setSearchQuery(query));
      // Перезагружаем уведомления с поиском
      const searchFilters = { ...filters, search: query };
      dispatch(fetchNotifications({ filters: searchFilters }));
    },
    [dispatch, filters],
  );

  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
    dispatch(fetchNotifications({}));
  }, [dispatch]);

  // UI управление
  const toggleNotificationPanel = useCallback(() => {
    dispatch(togglePanel());
  }, [dispatch]);

  const setNotificationPanelOpen = useCallback(
    (open: boolean) => {
      dispatch(setPanelOpen(open));
    },
    [dispatch],
  );

  const toggleSelection = useCallback(
    (notificationId: string) => {
      dispatch(toggleNotificationSelection(notificationId));
    },
    [dispatch],
  );

  const selectAll = useCallback(() => {
    dispatch(selectAllNotifications());
  }, [dispatch]);

  const clearSelections = useCallback(() => {
    dispatch(clearSelection());
  }, [dispatch]);

  // Toast управление
  const showToast = useCallback(
    (toast: Omit<IToastNotification, "id">) => {
      dispatch(addToast(toast));
    },
    [dispatch],
  );

  const hideToast = useCallback(
    (id: string) => {
      dispatch(removeToast(id));
    },
    [dispatch],
  );

  const clearToasts = useCallback(() => {
    dispatch(clearAllToasts());
  }, [dispatch]);

  // WebSocket управление
  const isConnected = useMemo(() => {
    return webSocketService.isConnected();
  }, []);

  const connectWebSocket = useCallback(() => {
    if (isAuthenticated) {
      webSocketService.connect();
    }
  }, [isAuthenticated]);

  const disconnectWebSocket = useCallback(() => {
    webSocketService.disconnect();
  }, []);

  // Утилиты
  const getUnreadByType = useCallback(
    (type: NotificationType): number => {
      return notifications.filter(
        (n) => n.type === type && !isNotificationRead(n),
      ).length;
    },
    [notifications],
  );

  const getNotificationsByStatus = useCallback(
    (status: NotificationStatus) => {
      return notifications.filter((n) => n.status === status);
    },
    [notifications],
  );

  const getNotificationsByPriority = useCallback(
    (priority: NotificationPriority) => {
      return notifications.filter((n) => n.priority === priority);
    },
    [notifications],
  );

  const clearErrors = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Дополнительные вычисляемые значения
  const hasSelectedNotifications = selectedNotifications.length > 0;
  const allNotificationsSelected =
    selectedNotifications.length === notifications.length &&
    notifications.length > 0;

  const notificationsByType = useMemo(() => {
    const result: { [key in NotificationType]?: number } = {};
    notifications.forEach((n) => {
      result[n.type] = (result[n.type] || 0) + 1;
    });
    return result;
  }, [notifications]);

  const notificationsByPriority = useMemo(() => {
    const result: { [key in NotificationPriority]?: number } = {};
    notifications.forEach((n) => {
      result[n.priority] = (result[n.priority] || 0) + 1;
    });
    return result;
  }, [notifications]);

  const unreadByType = useMemo(() => {
    const result: { [key in NotificationType]?: number } = {};
    notifications
      .filter((n) => !isNotificationRead(n))
      .forEach((n) => {
        result[n.type] = (result[n.type] || 0) + 1;
      });
    return result;
  }, [notifications]);

  // Браузерные уведомления
  const requestBrowserNotificationPermission = useCallback(async () => {
    if ("Notification" in window) {
      return await Notification.requestPermission();
    }
    return "denied" as NotificationPermission;
  }, []);

  // Запрос списка уведомлений
  const {
    data: notificationsResponse,
    isLoading: queryLoading,
    error: queryError,
  } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEYS.list(filters),
    queryFn: () => notificationService.getNotifications(filters),
    staleTime: 30 * 1000, // 30 секунд
  });

  // Запрос количества непрочитанных уведомлений
  const { data: unreadCountResponse = 0 } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEYS.unreadCount(),
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 10 * 1000, // 10 секунд
    refetchInterval: 30 * 1000, // Обновляем каждые 30 секунд
    retry: false, // не спамить при недоступности бэкенда (ECONNREFUSED)
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation<INotification, Error, string>({
    mutationFn: notificationService.markAsRead.bind(notificationService),
    onSuccess: (notification) => {
      queryClient.setQueryData(["notifications"], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          notifications: oldData.notifications.map((notif: any) =>
            notif.id === notification.id ? { ...notif, isRead: true } : notif,
          ),
        };
      });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation<void, Error, void>({
    mutationFn: notificationService.markAllAsRead.bind(notificationService),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Функция для обновления уведомлений
  const refreshNotifications = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: NOTIFICATIONS_QUERY_KEYS.list(filters),
    });
    queryClient.invalidateQueries({
      queryKey: NOTIFICATIONS_QUERY_KEYS.unreadCount(),
    });
  }, [queryClient, filters]);

  // Функция для загрузки дополнительных уведомлений (пагинация)
  const loadMore = useCallback(() => {
    if (notificationsResponse?.hasMore) {
      // Используем текущую страницу из пагинации
      const nextPage = (nextCursor || 1) + 1;
      // Здесь можно реализовать infinite query, но пока просто инвалидируем
      const filtersForAPI = filters;
      queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_QUERY_KEYS.list({
          ...filtersForAPI,
          page: nextPage,
        }),
      });
    }
  }, [queryClient, filters, notificationsResponse?.hasMore, nextCursor]);

  // Функция для получения уведомления по ID из кэша
  const getNotificationById = useCallback(
    (id: string): INotification | undefined => {
      return queryClient.getQueryData(NOTIFICATIONS_QUERY_KEYS.detail(id));
    },
    [queryClient],
  );

  return {
    // Данные
    notifications: notificationsResponse?.notifications || [],
    groups,
    unreadCount: unreadCountResponse,
    toasts,

    // Состояние
    isLoading: queryLoading || isLoading,
    connectionStatus,
    filters,
    uiSettings,

    // Действия с уведомлениями
    loadNotifications,
    loadMoreNotifications,
    markNotificationsAsRead,
    markAllNotificationsAsRead,
    deleteSelectedNotifications,

    // Настройки
    loadSettings,
    updateNotificationSettings,

    // Фильтрация и сортировка
    updateFilters,
    updateSearchQuery,
    resetFilters,

    // UI управление
    toggleNotificationPanel,
    setNotificationPanelOpen,
    toggleSelection,
    selectAll,
    clearSelections,

    // Toast управление
    showToast,
    hideToast,
    clearToasts,

    // WebSocket управление
    isConnected,
    connectWebSocket,
    disconnectWebSocket,

    // Утилиты
    getUnreadByType,
    getNotificationsByStatus,
    getNotificationsByPriority,
    clearErrors,

    // Дополнительные данные
    total: notificationsResponse?.total || total,
    hasMore: notificationsResponse?.hasMore || hasMore,
    nextCursor: nextCursor.toString(),
    settings: settings[0] || ({} as INotificationSettings),
    stats,
    error: error || (queryError as Error)?.message || null,
    panelOpen,
    selectedNotifications,
    hasSelectedNotifications,
    allNotificationsSelected,
    notificationsByType,
    notificationsByPriority,
    unreadByType,

    // Браузерные уведомления
    requestBrowserNotificationPermission,

    // Операции (React Query mutations)
    markAsRead: markAsReadMutation,
    markAllAsRead: markAllAsReadMutation,

    // Функции
    refreshNotifications,
    loadMore,
    getNotificationById,
  };
};

export default useNotifications;
